import React, { useState, useRef, useEffect } from 'react';
import { Layers, Image as ImageIcon, ChevronRight } from 'lucide-react';
import { NOISE_SVG_DATA_URI, STICKERS, POSTERS, LOOKBOOKS } from './constants';
import { useCart } from './CartContext';
import { flyToCart } from './cartAnimation';


// Components
import CustomCursor from './components/CustomCursor';
import Navbar from './components/Navbar';
import PosterRevealHero from './components/PosterRevealHero';
import HeroSection from './components/HeroSection';
import InfiniteMarquee from './components/InfiniteMarquee';
import DropCountdown from './components/DropCountdown';
import ProductCard from './components/ProductCard';
import ExploreCard from './components/ExploreCard';
// import ShopTheLookInteractive from './components/ShopTheLookInteractive';
import SystemTerminal from './components/SystemTerminal';
import UGCSection from './components/UGCSection';
import CustomerShowcase from './components/CustomerShowcase';
import AboutUs from './components/AboutUs';
import CategoryNav from './components/CategoryNav';
import CustomPosterSection from './components/CustomPosterSection';
import Footer from './components/Footer';
import PolicyPage from './components/PolicyPage';
import ContactPage from './components/ContactPage';
import SearchCommandPalette from './components/SearchCommandPalette';
import CollectionOverlay from './components/CollectionOverlay';
import CheckoutPage from './components/CheckoutPage';
import OrderHistory from './components/OrderHistory';
import OrderSuccess from './components/OrderSuccess';
import { CartDrawer } from './components/CartDrawer';


const App: React.FC = () => {
  
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCollectionOpen, setIsCollectionOpen] = useState(false);
  const [collectionInitialType, setCollectionInitialType] = useState('All');
  const { addToCart, cartItems, setIsCartOpen, clearCart } = useCart();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isOrderHistoryOpen, setIsOrderHistoryOpen] = useState(false);
  const [orderSuccessData, setOrderSuccessData] = useState<{ orderId: string; amount: number; items: { name: string; quantity: number }[] } | null>(null);
  const [activePolicyPage, setActivePolicyPage] = useState<'terms' | 'privacy' | 'refund' | 'shipping' | null>(null);
  const [isContactOpen, setIsContactOpen] = useState(false);

  // Lazy-loading gate for PosterRevealHero
  const posterSectionRef = useRef<HTMLDivElement>(null);
  const [shouldLoadPoster, setShouldLoadPoster] = useState(false);

  useEffect(() => {
    const el = posterSectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadPoster(true);
          observer.disconnect(); // Only need to trigger once
        }
      },
      {
        rootMargin: '600px 0px', // Start loading 600px before it enters viewport
        threshold: 0,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);


  // Show all 30 stickers as requested
  const featuredStickers = STICKERS.slice(0, 30);
  // Keep posters limited for focus
  const featuredPosters = POSTERS.slice(0, 12);

  const openCollection = (type: string) => {
    setCollectionInitialType(type);
    setIsCollectionOpen(true);
  };

  return (
    <div className="min-h-screen bg-neutral-900 text-white font-sans selection:bg-amber-500 selection:text-white cursor-none" style={{ overflowX: 'clip' }}>
      
      {/* --- Global Effects --- */}
      <div 
        className="fixed inset-0 pointer-events-none z-[60] mix-blend-overlay opacity-40"
        style={{ backgroundImage: `url("${NOISE_SVG_DATA_URI}")` }}
      ></div>

      <CustomCursor />

      {/* --- Navigation --- */}
      <Navbar 
        onOpenSearch={() => setIsSearchOpen(true)}
        onOpenCollection={() => openCollection('All')}
        onOpenOrders={() => setIsOrderHistoryOpen(true)}
      />

      {/* --- Overlays --- */}
      
      <SearchCommandPalette isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      <CollectionOverlay 
        isOpen={isCollectionOpen} 
        onClose={() => setIsCollectionOpen(false)} 
        initialType={collectionInitialType}
      />

      {/* --- Cart Drawer --- */}
       <CartDrawer onCheckout={() => setIsCheckoutOpen(true)} />

       <CheckoutPage
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        onSuccess={(data) => { clearCart(); setIsCheckoutOpen(false); setOrderSuccessData(data); }}
      />

      <OrderSuccess
        isOpen={orderSuccessData !== null}
        onClose={() => setOrderSuccessData(null)}
        orderDetails={orderSuccessData}
      />

      <OrderHistory
        isOpen={isOrderHistoryOpen}
        onClose={() => setIsOrderHistoryOpen(false)}
      />

      <PolicyPage
        isOpen={activePolicyPage !== null}
        onClose={() => setActivePolicyPage(null)}
        policyType={activePolicyPage || 'terms'}
      />

      {/* --- Main Document Landmark --- */}
      <main>
        {/* --- Hero Section --- */}
        <HeroSection />

        {/* --- Community Upgrades (moved up for faster engagement) --- */}
        <CustomerShowcase />


        {/* --- Infinite Marquee 1 --- */}
        <InfiniteMarquee text="ELEVATE YOUR REALITY • CUSTOM SKINS • LIMITED DROPS • ALL INDIA SHIPPING • DON'T BE BORING •" />

        {/* --- Drop Countdown --- */}
        <DropCountdown />

        {/* --- Collection Section --- */}
        <section id="collection" className="py-24 bg-neutral-900 relative">
          <div className="container mx-auto px-6">
            <div className="mb-20">
               <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-4">THE ARCHIVE</h2>
               <div className="h-1 w-24 bg-amber-500 mb-6"></div>
               <p className="text-neutral-400 uppercase tracking-widest text-xs font-bold">Featured artifacts from the current timeline.</p>
            </div>

            {/* Posters - Matching Sector label */}
            <div className="mb-24">
              <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-4 border-b border-white/10 pb-4">
                <div className="flex items-center gap-3">
                  <ImageIcon className="text-amber-500 w-6 h-6" />
                  <h3 className="text-2xl font-bold tracking-widest uppercase italic">Wall Art</h3>
                </div>
                <span className="text-[10px] font-mono text-neutral-500 uppercase">Sector: 02_Prints</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {featuredPosters.map((product) => (
                  <ProductCard 
                    key={product.id} 
                    product={product} 
                    onOrder={(e) => {
                      flyToCart(e, product.imageAfter);
                      addToCart(product);
                    }} 
                  />
                ))}
                <ExploreCard 
                  title="Gallery" 
                  subtitle="Browse All Prints" 
                  onClick={() => openCollection('Poster')}
                  bgImage="https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=2070&auto=format&fit=crop"
                />
              </div>
            </div>
          </div>
        </section>

        {/* --- Category Quick Nav --- */}
        <CategoryNav onOpenCollection={(type) => openCollection(type)} />

        {/* --- Custom Poster / Sticker Request --- */}
        <CustomPosterSection />

        {/* --- Manifesto --- */}
        <section id="vibe" className="py-24 bg-neutral-900 border-b border-t border-white/5 relative overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-900/10 rounded-full blur-[120px] pointer-events-none"></div>
          <div className="container mx-auto px-6 text-center max-w-4xl relative z-10">
            <h2 className="text-xs font-bold tracking-[0.3em] text-amber-500 mb-6 uppercase">The Philosophy</h2>
            <p className="text-4xl md:text-6xl font-black leading-tight mb-8 tracking-tighter">
              "DEFAULT IS <span className="text-amber-500">DEATH.</span>"
            </p>
            <p className="text-neutral-400 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Your devices are an extension of your mind. Why leave them factory standard? 
              We create aesthetic upgrades for the new generation. Not just stickers—identity layers.
            </p>
          </div>
        </section>

        {/* --- Stickers & Skins Section --- */}
        <section id="stickers" className="pb-24 pt-12 bg-neutral-900 relative">
          <div className="container mx-auto px-6">
            {/* Stickers & Skins - Horizontal Scroll */}
            <div>
              <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-4 border-b border-white/10 pb-4">
                <div className="flex items-center gap-3">
                  <Layers className="text-amber-500 w-6 h-6" />
                  <h3 className="text-2xl font-bold tracking-widest uppercase italic">Stickers & Skins</h3>
                </div>
                <div className="flex items-center gap-3 md:gap-6">
                  <div className="flex items-center gap-1 md:gap-2 text-[10px] text-amber-400 font-bold uppercase tracking-widest animate-pulse">
                    <span className="md:hidden">Swipe</span>
                    <span className="hidden md:inline">Scroll Right</span>
                    <ChevronRight className="w-3 h-3" />
                  </div>
                  <span className="text-[10px] font-mono text-neutral-500 uppercase">Sector: 01_Skins</span>
                </div>
              </div>
              
              <div className="relative group/scroll">
                {/* Fade out mask on the right */}
                <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-neutral-900 to-transparent z-10 pointer-events-none opacity-0 md:group-hover/scroll:opacity-100 transition-opacity"></div>
                
                <div className="flex overflow-x-auto no-scrollbar gap-4 pb-6 px-1 scroll-smooth">
                  {featuredStickers.map((product) => (
                    <div key={product.id} className="flex-none w-[160px] md:w-[220px]">
                      <ProductCard 
                        product={product} 
                        onOrder={(e) => {
                          flyToCart(e, product.imageAfter);
                          addToCart(product);
                        }} 
                      />
                    </div>
                  ))}
                  {/* Explore More Card at the end of the scroll */}
                  <div className="flex-none w-[160px] md:w-[220px]">
                    <ExploreCard 
                      title="Archive" 
                      subtitle="View Full Archive" 
                      onClick={() => openCollection('Stickers')}
                      bgImage="https://images.unsplash.com/photo-1614624532983-4ce03382d63d?q=80&w=2070&auto=format&fit=crop"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --- Infinite Marquee 2 --- */}
        <InfiniteMarquee text="EST 2024 • DESIGNED BY THE IDEAL FIRM • MANUFACTURED ON EARTH • FUTURE READY • YUMECAV •" reverse />

        {/* --- Cinematic Poster Reveal (lazy-loaded) --- */}
        <div ref={posterSectionRef}>
          <PosterRevealHero shouldLoad={shouldLoadPoster} />
        </div>

        {/* --- About Us --- */}
        <AboutUs />

        {/* --- System Terminal --- */}
        <SystemTerminal />

        {/* --- UGC Grid --- */}
        <UGCSection />
      </main>

      {/* --- Footer --- */}
      <Footer
        onOpenOrders={() => setIsOrderHistoryOpen(true)}
        onOpenPolicy={(type) => setActivePolicyPage(type)}
        onOpenContact={() => setIsContactOpen(true)}
      />

      <ContactPage
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />
    </div>
  );
};

export default App;